import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AISettingsComponent from './SettingsTabs/AISettings';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import ThemeToggle from '../shared/TailwindComponents/ThemeToggle';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { ThemeContext } from '../../contexts/ThemeContext';
import { changeLanguage } from '../../i18n/i18n';
import { updateGeneralSettings, initializeUserPreferencesIfNeeded } from '../../store/actions/SettingsActions';
import { getUserType, isBetaEnv } from '../../utils/authUtils';
import { getWhiteLabelSettings } from '../../store/actions/organization/WhiteLabelActions';
import { getUserID } from '../../store/actions/ChatAction';
import {
  PiBellLight,
  PiBuildingLight,
  PiUserCheckLight,
  PiShieldCheckLight,
  PiSignatureLight,
  PiListChecksLight,
  PiSparkleLight,
  PiFadersHorizontalLight,
  PiCreditCardLight,
  PiTrashLight,
  PiPuzzlePieceLight,
} from 'react-icons/pi';
import PlansPage from '../shared/Licenses/PlansPage';
import SignatureModal from './Security/DigitalSignature';
import IdentityVerificationTab from './SettingsTabs/IDVerification';
import LoginSecurityTab from './SettingsTabs/LoginSecurityTab';
import WhiteLabelSettings from './WhiteLabelSettings';
import NotificationSettings from './SettingsTabs/NotificationsTab';
import OrganizationSettings from './SettingsTabs/OrganizationSettings';
import TaskSettings from './SettingsTabs/TaskSettings';
import RecycleBinPage from '../RecycleBin/RecycleBinPage';
import { currencies } from '../../utils/functions';
import FilterBarWithTabs, { TabItem } from '../shared/TailwindComponents/FilterBarWithTabs';
import { useLocation, useHistory } from 'react-router-dom';
import Header from '../shared/TailwindComponents/Header';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, settingsPageTourSteps } from '../shared/FeatureTour';
// import IntegrationsTab from './SettingsTabs/IntegrationsTab';

type TabType =
  | 'general'
  | 'ai'
  // | 'branding'
  // | 'signature'
  | 'identity'
  | 'integrations'
  // | 'loginSecurity'
  | 'notifications'
  | 'organization'
  | 'tasks'
  | 'plans'
  | 'recycle_bin';

const SUPPORTED_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'sv', label: 'Svenska' },
  { value: 'es', label: 'Español' },
  { value: 'nb', label: 'Norsk Bokmål' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fi', label: 'Suomi' },
  { value: 'da', label: 'Dansk' },
  { value: 'he', label: 'עברית' },
];

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('tab') as TabType) || 'general';
  });
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const generalSettings = userInfo?.preferences?.general_settings || {};
  const { theme } = useContext(ThemeContext);
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const [currencyList, setCurrencyList] = useState<{ value: string; label: string; symbol: string }[]>([]);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'settings-page',
    steps: settingsPageTourSteps,
    autoStartDelay: 600,
  });

  // Initialize preferences on component mount
  useEffect(() => {
    dispatch(initializeUserPreferencesIfNeeded() as any);
  }, [dispatch]);

  // Initialize component with actual values from preferences
  useEffect(() => {
    const fetchCurrencies = async () => {
      setCurrencyList(currencies);
    };
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as TabType;
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  const handleLanguageChange = (selectedValues: string[] | string) => {
    const selectedId = Array.isArray(selectedValues) ? selectedValues[0] : selectedValues;
    const selectedOption = SUPPORTED_LANGUAGES.find((option) => option.value === selectedId);
    if (selectedOption) {
      changeLanguage(selectedOption.value);
      dispatch(
        updateGeneralSettings({
          language: selectedOption.value,
          theme: generalSettings?.theme || 'light',
          currency: generalSettings?.currency || 'EUR',
        }) as any
      );
    }
  };

  const handleCurrencyChange = (selectedValue: string | string[]) => {
    const selectedCurrency = currencyList.find((currency) => currency.value === selectedValue);
    if (selectedCurrency) {
      dispatch(
        updateGeneralSettings({
          language: generalSettings?.language || 'en',
          theme: generalSettings?.theme || 'light',
          currency: selectedCurrency.value,
        }) as any
      );
    }
  };

  const tabs: TabItem[] = [
    { label: t('General'), value: 'general', icon: <PiFadersHorizontalLight size={16} /> },
    { label: t('AI'), value: 'ai', icon: <PiSparkleLight size={16} /> },
    { label: t('Notifications'), value: 'notifications', icon: <PiBellLight size={16} /> },
    // Integrations tab hidden while Google Drive / OneDrive OAuth flow is incomplete.
    // { label: t('Integrations'), value: 'integrations', icon: <PiPuzzlePieceLight size={16} /> },
    ...(userInfo?.is_org_admin ? [{ label: t('Organization'), value: 'organization', icon: <PiBuildingLight size={16} /> }] : []),
    { label: t('Recycle Bin'), value: 'recycle_bin', icon: <PiTrashLight size={16} /> },
    // { label: t('Plans & Billing'), value: 'plans', icon: <PiCreditCardLight size={16} /> },
    // { label: 'Signature', value: 'signature', icon: <PiSignatureLight size={16} /> },
    // {
    //   label: 'Branding',
    //   value: 'branding',
    //   icon: <PiBuildingLight size={16} />,
    //   disabled: !subscriptionActive,
    //   tooltip: !subscriptionActive ? 'Premium Feature' : undefined,
    //   lock: !subscriptionActive,
    // },
    // { label: 'Login Security', value: 'loginSecurity', icon: <PiShieldCheckLight size={16} /> },
    // { label: 'ID Verification', value: 'identity', icon: <PiUserCheckLight size={16} /> },
    // { label: 'Tasks', value: 'tasks', icon: <PiListChecksLight size={16} /> },
  ];

  const handleTabClick = (value: string) => {
    setActiveTab(value as TabType);
    const params = new URLSearchParams(location.search);
    params.set('tab', value);
    history.push({ search: params.toString() });
  };

  return (
    <div className="flex flex-col h-full">
      <Header />
      <div data-tour-id="settings-tabs">
        <FilterBarWithTabs tabs={tabs} activeTab={activeTab} onTabClick={handleTabClick} />
      </div>
      <div data-tour-id="settings-content" className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div className="space-y-2 px-4 pt-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t('General Settings')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('Customize your platform experience with these settings.')}
              </p>
            </div>

            <hr className="border-gray-200 dark:border-gray-700" />

            <div data-tour-id="settings-cards" className="grid grid-cols-2 2xl:grid-cols-3 gap-4 items-start px-4">
              <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                <div className="p-4">
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1">
                        {t('Language')}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('Select your preferred language.')}
                      </p>
                    </div>
                    <div className="mt-4">
                      <Dropdown
                        options={SUPPORTED_LANGUAGES}
                        value={generalSettings?.language || 'en'}
                        placeholder={t('Select Language')}
                        onChange={handleLanguageChange}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                <div className="p-4">
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1">
                        {t('Theme')}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('Select your preferred theme or auto-switch with System.')}
                      </p>
                    </div>
                    <div className="mt-4 w-full">
                      <ThemeToggle />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                <div className="p-4">
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1">
                        {t('Currency')}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('Select your preferred currency.')}
                      </p>
                    </div>
                    <div className="mt-4">
                      <Dropdown
                        options={currencyList}
                        value={generalSettings?.currency || 'EUR'}
                        placeholder={t('Select Currency')}
                        onChange={handleCurrencyChange}
                        className="w-full"
                        renderOption={(option: any) => (
                          <div className="flex items-center">
                            <span className="mr-2">{option.symbol}</span>
                            {option.label}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'ai' && <AISettingsComponent />}
        {activeTab === 'notifications' && <NotificationSettings />}
        {activeTab === 'organization' && userInfo?.is_org_admin && <OrganizationSettings />}
        {activeTab === 'recycle_bin' && <RecycleBinPage />}
        {/* {activeTab === 'integrations' && <IntegrationsTab />} */}
        {/* {activeTab === 'plans' && <PlansPage />} */}
        {/* {activeTab === 'branding' && <WhiteLabelSettings />} */}
        {/* {activeTab === 'signature' && <SignatureModal onSave={() => {}} onConfirm={() => {}} />} */}
        {/* {activeTab === 'loginSecurity' && <LoginSecurityTab />} */}
        {/* {activeTab === 'identity' && <IdentityVerificationTab />} */}
        {/* {activeTab === 'tasks' && <TaskSettings />} */}
      </div>
      <FeatureTour
        steps={settingsPageTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default SettingsPage;
